import { Chip as ChipMaterial, ChipProps as ChipMaterialProps } from "@mui/material"
import React, { useEffect, useState } from "react"
import styled, { css } from "styled-components"
import { theme } from "../../../theme"
import { Tooltip } from "../../common/Tooltip"
import { formatFullName } from "../../formatters"
import { Avatar, AvatarProps, AvatarSize } from "../avatar"
import { ArrowDropDown, ArrowDropUp, Close } from "@mui/icons-material"

export enum ChipAction {
  Clickable = "clickable",
  Dismissible = "dismissible",
  Dropdown = "dropdown",
}

export enum ChipSize {
  Small = "small",
  Big = "big",
}

type PropsFromMui = "disabled" | "icon" | "label" | "onDelete" | "ref"

export type ChipProps = Pick<ChipMaterialProps, PropsFromMui> & {
  avatar?: AvatarProps
  action?: ChipAction
  active?: boolean
  size?: ChipSize
  value?: string | number
  dataCy?: string
  onClick?: (event: React.MouseEvent<HTMLDivElement>, checked?: boolean) => void
}

export const Chip = (props: ChipProps) => {
  const { icon, avatar, label, action, dataCy, onDelete, size, onClick, active, ...rest } = props

  const [selected, setSelected] = useState<boolean>(false)
  const clickable = action === ChipAction.Clickable

  useEffect(() => {
    setSelected(!!active)
  }, [active])

  const renterTooltipTitle = () => {
    if (avatar?.individual?.organization) {
      return (
        <>
          {formatFullName(avatar.individual.firstName, avatar.individual.lastName, avatar.individual.title)}
          <TooltipTitle>{avatar.individual.organization}</TooltipTitle>
        </>
      )
    }
    return ""
  }

  const renderChipLabel = () => {
    if (avatar) {
      return (
        <ChipLabelWrapper>
          <Tooltip title={renterTooltipTitle()}>
            <ChipLabel>
              {formatFullName(avatar.individual.firstName, avatar.individual.lastName, avatar.individual.title)}
            </ChipLabel>
          </Tooltip>
          {action === ChipAction.Dropdown && (
            <IconWrapper $size={size} $active={selected} $arrowDropDown={true}>
              <ArrowDropDown />
            </IconWrapper>
          )}
        </ChipLabelWrapper>
      )
    }

    return (
      <ChipLabel>
        {label}
        {action === ChipAction.Dropdown && (
          <IconWrapper $size={size} $active={selected} $arrowDropDown={true}>
            {!!selected ? <ArrowDropUp /> : <ArrowDropDown />}
          </IconWrapper>
        )}
      </ChipLabel>
    )
  }

  const renderChipIcon = () => {
    if (avatar) {
      return <Avatar {...avatar} size={AvatarSize.small} />
    }
    if (icon) {
      return (
        <IconWrapper $size={size} $active={selected}>
          {icon}
        </IconWrapper>
      )
    }
    return undefined
  }

  const handleClick = (e: React.MouseEvent<HTMLDivElement>) => {
    const newVal = clickable ? !selected : undefined

    e.preventDefault()

    if (clickable) {
      setSelected(!!newVal)
    }
    if (onClick) {
      onClick(e, !!newVal)
    }
  }

  return (
    <CustomChip
      {...rest}
      $size={size}
      $active={selected}
      icon={renderChipIcon()}
      deleteIcon={<DeleteIcon $active={selected} />}
      clickable={!!action && [ChipAction.Clickable, ChipAction.Dropdown].includes(action)}
      label={renderChipLabel()}
      onClick={handleClick}
      data-cy={dataCy}
      onDelete={action !== ChipAction.Dropdown ? onDelete : undefined}
    />
  )
}

const getNormalStyle = (clickable: boolean) => css`
  color: ${theme.color.black6};
  background-color: ${theme.color.grey5};

  &:focus {
    color: ${theme.color.black6};
    background-color: ${theme.color.grey5};
  }

  &:hover {
    background-color: ${clickable ? theme.color.white2 : theme.color.grey5};
  }
`

const getActiveStyle = (clickable: boolean) => css`
  color: ${theme.color.white};
  background-color: ${theme.color.black4};

  &:focus {
    color: ${theme.color.white};
    background-color: ${theme.color.black4};
  }

  &:hover {
    background-color: ${clickable ? theme.color.grey2 : theme.color.black4};
  }
`

const smallIcon = css`
  width: ${theme.iconSize.xs};
  height: ${theme.iconSize.xs};
`

const bigIcon = css`
  width: ${theme.iconSize.l};
  height: ${theme.iconSize.l};
`

const small = css`
  height: ${theme.iconSize.xs};
  padding: 0 9px;
  grid-gap: ${theme.spacing.xs};
  font-size: ${theme.fontSize.s};
  line-height: ${theme.lineHeight.s};
`

const big = css`
  height: ${theme.iconSize.xl};
  padding: 0 12px;
  grid-gap: ${theme.spacing.s};
  font-size: ${theme.fontSize.m};
  line-height: ${theme.lineHeight.m};
  color: ${theme.color.grey1};
`

const ChipLabelWrapper = styled.div`
  display: grid;
  grid-auto-flow: column;
`

const ChipLabel = styled.div<{ $size?: ChipSize }>`
  display: flex;
  align-items: center;
  gap: ${(p) => (p.$size === ChipSize.Small ? theme.spacing.xs : theme.spacing.s)};
`

const CustomChip = styled(ChipMaterial)<{ $active: boolean; $size?: ChipSize }>`
  display: inline-grid;
  grid-auto-flow: column;

  ${(p) => (p.$size === ChipSize.Small ? small : big)}
  ${(p) => (p.$active ? getActiveStyle(!!p.onClick) : getNormalStyle(!!p.onClick))}

  * {
    margin: 0;
  }

  .MuiChip-label,
  .MuiChip-icon,
  .MuiChip-deleteIcon {
    padding: 0;
    margin: 0;
  }

  &:active {
    box-shadow: none;
  }
`

const DeleteIcon = styled(Close)<{ $active: boolean }>`
  && {
    width: ${theme.fontSize.s};
    height: ${theme.fontSize.s};
    color: ${(props) => (props.$active === true ? theme.color.white : theme.color.grey1)};
  }
`
const getActiveIconColor = (arrowDropDown?: boolean) => (arrowDropDown ? theme.color.grey1 : theme.color.grey2)

const IconWrapper = styled.div<{ $active: boolean; $size?: ChipSize; $arrowDropDown?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;

  svg {
    padding: 0;
    ${(p) => (p.$size === "small" ? smallIcon : bigIcon)}
    color: ${(props) => (props.$active === true ? theme.color.white : getActiveIconColor(props.$arrowDropDown))};
  }
`

const TooltipTitle = styled.div`
  font-size: ${theme.fontSize.m};
`

export const ChipButton = (props: ChipProps) => <Chip {...props} action={ChipAction.Clickable} />
